import { Link } from 'react-router-dom';

interface MovieItem {
  id: number;
  title: string;
  year: number;
  category: string;
  rating: number;
  region: string;
  imageUrl: string;
  tags?: string[];
  type?: string;
  episodes?: string;
}

interface MovieGridProps {
  title: string;
  subtitle?: string;
  movies: MovieItem[];
  showType?: boolean;
}

export default function MovieGrid({ title, subtitle, movies, showType = false }: MovieGridProps) {
  return (
    <section className="w-full py-12 md:py-16">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {title}
            </h2>
            {subtitle && (
              <p className="text-foreground-500 text-sm">{subtitle}</p>
            )}
          </div>
          <Link
            to="/category/movie"
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            查看更多
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </div>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {movies.map((movie) => (
            <Link
              key={movie.id}
              to={`/detail/${movie.id}`}
              className="group cursor-pointer"
            >
              <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                <img
                  src={movie.imageUrl}
                  alt={movie.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                  loading="lazy"
                />
                <div className="absolute top-2 right-2 flex items-center gap-0.5 px-2 py-0.5 bg-black/60 backdrop-blur-sm rounded-md text-yellow-400 text-xs font-medium">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-star-fill text-[10px]"></i>
                  </div>
                  {movie.rating}
                </div>
                {movie.tags && movie.tags.length > 0 && (
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                          tag === '热门'
                            ? 'bg-accent-500 text-white'
                            : tag === '新片'
                              ? 'bg-primary-500 text-white'
                              : tag === '高分'
                                ? 'bg-secondary-500 text-white'
                                : 'bg-foreground-800 text-white'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {showType && movie.type && (
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 backdrop-blur-sm rounded text-white text-[10px] font-medium">
                    {movie.type}
                  </div>
                )}
              </div>

              <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                {movie.title}
              </h3>
              <div className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
                <span>{movie.year}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{movie.region}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="text-foreground-500">{movie.category}</span>
              </div>
              {showType && movie.episodes && (
                <p className="text-xs text-primary-500 mt-0.5">{movie.episodes}</p>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}